import clsx from 'clsx'
import {Dispatch, FC, SetStateAction, useMemo} from 'react'
import {CustomInputs} from '../../../../../../_metronic/helpers/components/CustomInputs'
import {InvoiceTypeList} from '../../../invoice-management/invoices-list/core/_models'
import {AutomaticInvoiceTemplateSetting} from '../core/_models'
import {getAutomaticInvoiceTemplateLimit} from './automaticInvoiceTemplateLimits'

const supportedTemplateIds = [1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 13, 14]

const fallbackTemplateLabels: Record<number, string> = {
  1: 'الگوی فروش',
  2: 'فروش ارز',
  3: 'طلا و جواهر',
  4: 'پیمانکاری',
  5: 'قبوض خدماتی',
  6: 'بلیط هواپیما',
  7: 'صادرات',
  8: 'بارنامه',
  9: 'فرآورده‌های نفتی',
  11: 'بورس کالا',
  13: 'خدمات بیمه',
  14: 'فروش زنجیره',
}

const getNumberValue = (value: string | number | undefined | null) => {
  const normalized = String(value ?? '').replace(/,/g, '')
  const numberValue = Number(normalized || 0)
  return Number.isFinite(numberValue) ? numberValue : NaN
}

const getPercentPayload = (value: string | number | undefined | null) => {
  const numberValue = getNumberValue(value)
  return Number.isFinite(numberValue) ? numberValue : 0
}

const getTemplateOptions = () => {
  const map = new Map<
    number,
    {id: number; shortName?: string; name?: string; icon?: string; color?: string}
  >()

  InvoiceTypeList.forEach((invoiceType) => {
    invoiceType.template?.forEach((template) => {
      if (supportedTemplateIds.includes(template.id) && !map.has(template.id)) {
        map.set(template.id, template)
      }
    })
  })

  return supportedTemplateIds.map((id) => ({
    id,
    shortName: map.get(id)?.shortName || fallbackTemplateLabels[id] || `الگوی ${id}`,
    name: map.get(id)?.name || fallbackTemplateLabels[id] || `الگوی ${id}`,
    icon: map.get(id)?.icon || 'la-file-invoice',
    color: map.get(id)?.color || 'primary',
  }))
}

const getBadgeColor = (color?: string) =>
  ['primary', 'success', 'warning', 'info', 'danger'].includes(color || '') ? color : 'primary'

export const getDefaultAutomaticInvoiceTemplateSettings = (): AutomaticInvoiceTemplateSetting[] => [
  {
    tmp: 1,
    wgp: 0,
    prp: 0,
    cmp: 0,
  },
]

export const normalizeAutomaticInvoiceTemplateSettings = (
  settings?: AutomaticInvoiceTemplateSetting[],
  enabled = false
): AutomaticInvoiceTemplateSetting[] => {
  const normalized = (settings || [])
    .filter((item) => item && supportedTemplateIds.includes(Number(item.tmp || 0)))
    .reduce<AutomaticInvoiceTemplateSetting[]>((result, item) => {
      const templateId = Number(item.tmp)
      if (!result.some((selected) => Number(selected.tmp) === templateId)) {
        result.push({
          id: item.id,
          tmp: templateId,
          wgp: templateId === 3 ? item.wgp ?? 0 : 0,
          prp: templateId === 3 ? item.prp ?? 0 : 0,
          cmp: templateId === 3 ? item.cmp ?? 0 : 0,
        })
      }
      return result
    }, [])
    .sort(
      (left, right) =>
        supportedTemplateIds.indexOf(Number(left.tmp)) -
        supportedTemplateIds.indexOf(Number(right.tmp))
    )

  return enabled && normalized.length === 0
    ? getDefaultAutomaticInvoiceTemplateSettings()
    : normalized
}

export const getAutomaticInvoiceTemplateSettingsPayload = (
  settings?: AutomaticInvoiceTemplateSetting[],
  enabled = false
): AutomaticInvoiceTemplateSetting[] =>
  normalizeAutomaticInvoiceTemplateSettings(settings, enabled).map((item) => {
    const templateId = Number(item.tmp)
    return {
      id: item.id,
      tmp: templateId,
      wgp: templateId === 3 ? getPercentPayload(item.wgp) : 0,
      prp: templateId === 3 ? getPercentPayload(item.prp) : 0,
      cmp: templateId === 3 ? getPercentPayload(item.cmp) : 0,
    }
  })

export const getAutomaticInvoiceTemplateSettingsIsInvalid = (
  settings?: AutomaticInvoiceTemplateSetting[],
  enabled = false
) => {
  if (!enabled) {
    return false
  }

  const normalized = normalizeAutomaticInvoiceTemplateSettings(settings)
  if (normalized.length === 0) {
    return true
  }

  return normalized.some((item) => {
    if (Number(item.tmp) !== 3) {
      return false
    }

    return [item.wgp, item.prp, item.cmp].some((percent) => {
      const numberValue = getNumberValue(percent)
      return !Number.isFinite(numberValue) || numberValue < 0 || numberValue > 100
    })
  })
}

type Props = {
  value?: AutomaticInvoiceTemplateSetting[]
  onChange?: Dispatch<SetStateAction<AutomaticInvoiceTemplateSetting[]>>
  disabled?: boolean
  readOnly?: boolean
  compactReadOnly?: boolean
  error?: boolean
}

const AutomaticInvoiceTemplateSettings: FC<Props> = ({
  value,
  onChange,
  disabled = false,
  readOnly = false,
  compactReadOnly = false,
  error = false,
}) => {
  const templateOptions = useMemo(getTemplateOptions, [])
  const selectedSettings = normalizeAutomaticInvoiceTemplateSettings(value)
  const canEdit = !disabled && !readOnly

  const emit = (nextValue: AutomaticInvoiceTemplateSetting[]) => {
    if (canEdit) {
      onChange?.(normalizeAutomaticInvoiceTemplateSettings(nextValue))
    }
  }

  const toggleTemplate = (templateId: number) => {
    const exists = selectedSettings.some((item) => Number(item.tmp) === templateId)
    const nextValue = exists
      ? selectedSettings.filter((item) => Number(item.tmp) !== templateId)
      : [
          ...selectedSettings,
          {
            tmp: templateId,
            wgp: 0,
            prp: 0,
            cmp: 0,
          },
        ]

    emit(nextValue)
  }

  const updateGoldPercent = (
    field: 'wgp' | 'prp' | 'cmp',
    nextPercent: SetStateAction<string | number | undefined>
  ) => {
    const goldSetting = selectedSettings.find((item) => Number(item.tmp) === 3) || {tmp: 3}
    const currentValue = goldSetting[field] ?? ''
    const nextValue = typeof nextPercent === 'function' ? nextPercent(currentValue) : nextPercent

    emit(
      selectedSettings.map((item) =>
        Number(item.tmp) === 3
          ? {
              ...item,
              [field]: nextValue,
            }
          : item
      )
    )
  }

  const goldSetting = selectedSettings.find((item) => Number(item.tmp) === 3)
  const limitedSelections = selectedSettings
    .map((setting) => ({
      template: templateOptions.find((item) => item.id === Number(setting.tmp)),
      limit: getAutomaticInvoiceTemplateLimit(Number(setting.tmp)),
    }))
    .filter((item) => item.template && item.limit)

  if (readOnly && compactReadOnly) {
    return (
      <div className='mp-template-settings mp-template-settings--compact'>
        <div className='d-flex align-items-center flex-wrap gap-2'>
          {selectedSettings.length === 0 && (
            <span className='badge badge-light'>الگویی انتخاب نشده</span>
          )}
          {selectedSettings.map((setting) => {
            const template = templateOptions.find((item) => item.id === Number(setting.tmp))
            return (
              <span
                className={clsx('badge', `badge-light-${getBadgeColor(template?.color)}`)}
                key={setting.tmp}
                title={getAutomaticInvoiceTemplateLimit(Number(setting.tmp))?.note}
              >
                {template?.shortName || `الگوی ${setting.tmp}`}
                {Number(setting.tmp) === 3
                  ? ` | اجرت ${setting.wgp || 0}%، سود ${setting.prp || 0}%، حق‌العمل ${
                      setting.cmp || 0
                    }%`
                  : ''}
              </span>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className='mp-template-settings'>
      <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3'>
        <label className='form-label fw-bold mb-0'>الگوهای صورتحساب خودکار</label>
        <span className={clsx('badge', error ? 'badge-light-danger' : 'badge-light-primary')}>
          {selectedSettings.length} الگو
        </span>
      </div>

      <div className='row g-3'>
        {templateOptions.map((template) => {
          const isSelected = selectedSettings.some((item) => Number(item.tmp) === template.id)
          const limit = getAutomaticInvoiceTemplateLimit(template.id)
          return (
            <div className='col-xl-3 col-lg-4 col-md-6 col-12' key={template.id}>
              <button
                type='button'
                className={clsx(
                  'mp-template-option',
                  isSelected && 'is-selected',
                  readOnly && 'is-readonly',
                  error && !isSelected && 'is-muted'
                )}
                disabled={!canEdit}
                title={limit?.note}
                onClick={() => toggleTemplate(template.id)}
              >
                <span className={clsx('mp-template-option__icon', `text-${template.color}`)}>
                  <i className={clsx('las fs-2', template.icon)} />
                </span>
                <span className='mp-template-option__text'>
                  <strong>{template.shortName}</strong>
                  <small className={clsx(limit && 'text-warning')}>
                    الگوی {template.id}
                    {limit && ` — ${limit.label}`}
                  </small>
                </span>
                <span
                  className={clsx(
                    'mp-template-option__check',
                    isSelected ? 'bg-primary text-white' : 'bg-light text-gray-500'
                  )}
                >
                  <i className={clsx('las', isSelected ? 'la-check' : 'la-plus')} />
                </span>
              </button>
            </div>
          )
        })}
      </div>

      {limitedSelections.length > 0 && (
        <div className='alert alert-warning mt-4 mb-0 py-3' role='status'>
          <div className='fw-bold mb-1'>
            این الگوها ذخیره می‌مانند ولی در صدور خودکار محدودیت دارند:
          </div>
          <ul className='mb-0 ps-5'>
            {limitedSelections.map(({template, limit}) => (
              <li key={template?.id}>
                {template?.shortName}: {limit?.note}
              </li>
            ))}
          </ul>
        </div>
      )}

      {goldSetting && (
        <div className='mp-template-gold-settings mt-4'>
          <div className='row g-4'>
            <div className='col-lg-4 col-md-6'>
              <CustomInputs
                type='number'
                numberAsText
                label='درصد اجرت ساخت'
                isDisabled={!canEdit}
                stateValue={{
                  value: goldSetting.wgp ?? '',
                  setValue: (nextValue: SetStateAction<string | number | undefined>) =>
                    updateGoldPercent('wgp', nextValue),
                }}
              />
            </div>
            <div className='col-lg-4 col-md-6'>
              <CustomInputs
                type='number'
                numberAsText
                label='درصد سود فروشنده'
                isDisabled={!canEdit}
                stateValue={{
                  value: goldSetting.prp ?? '',
                  setValue: (nextValue: SetStateAction<string | number | undefined>) =>
                    updateGoldPercent('prp', nextValue),
                }}
              />
            </div>
            <div className='col-lg-4 col-md-6'>
              <CustomInputs
                type='number'
                numberAsText
                label='درصد حق‌العمل'
                isDisabled={!canEdit}
                stateValue={{
                  value: goldSetting.cmp ?? '',
                  setValue: (nextValue: SetStateAction<string | number | undefined>) =>
                    updateGoldPercent('cmp', nextValue),
                }}
                error={
                  error ? (
                    <span className='fs-7 text-danger'>
                      درصدهای واردشده باید بین ۰ تا ۱۰۰ باشند؛ فیلد خالی صفر در نظر گرفته می‌شود.
                    </span>
                  ) : undefined
                }
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export {AutomaticInvoiceTemplateSettings}
